import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ContainerCard } from "../../components/ui/cards";
import { Table, Tag, Badge, DatePicker, Select, Modal, Input, Spin } from "antd";
import { Phone, PhoneCall } from "lucide-react";
import { Button } from "../../components/ui/button";
import useApiMethods from "../../lib/useApiMethods";
import { AI_CALLER_CALLS_URL, AI_CALLER_CALL_INITIATE_URL } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import dayjs from "dayjs";
import CallCostBreakdown from "../engage/ai-caller/CallCostBreakdown";
import { formatInr } from "../engage/ai-caller/aiCallerConstants";

const { RangePicker } = DatePicker;

// ── helpers ───────────────────────────────────────────────────────────────────

const OUTCOME_COLORS = {
    demo_booked: "green",
    qualified: "blue",
    disqualified: "red",
    no_answer: "default",
    callback_requested: "orange",
};

const STATUS_BADGE = {
    in_progress: "processing",
    completed: "success",
    failed: "error",
};

const STATUS_OPTIONS = [
    { value: "", label: "All Statuses" },
    { value: "in_progress", label: "In Progress" },
    { value: "completed", label: "Completed" },
    { value: "failed", label: "Failed" },
];

const OUTCOME_OPTIONS = [
    { value: "", label: "All Outcomes" },
    { value: "demo_booked", label: "Demo Booked" },
    { value: "qualified", label: "Qualified" },
    { value: "disqualified", label: "Disqualified" },
    { value: "no_answer", label: "No Answer" },
    { value: "callback_requested", label: "Callback Requested" },
];

const formatDuration = (seconds) => {
    if (!seconds) return "—";
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m ${s}s`;
};

// ── main component ────────────────────────────────────────────────────────────

export default function AICallerCallLogs() {
    const navigate = useNavigate();
    const { fetch, create } = useApiMethods();

    const [calls, setCalls] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(1);
    const [limit] = useState(20);

    // filters
    const [dateRange, setDateRange] = useState(null);
    const [statusFilter, setStatusFilter] = useState("");
    const [outcomeFilter, setOutcomeFilter] = useState("");

    // initiate call modal
    const [callModalOpen, setCallModalOpen] = useState(false);
    const [customerId, setCustomerId] = useState("");
    const [initiating, setInitiating] = useState(false);

    // per-call cost breakdown modal
    const [costModalCall, setCostModalCall] = useState(null);

    const loadCalls = async () => {
        setLoading(true);
        const filters = { page, limit };
        if (statusFilter) filters.status = statusFilter;
        if (outcomeFilter) filters.outcome = outcomeFilter;
        if (dateRange && dateRange[0] && dateRange[1]) {
            filters.from_date = dateRange[0].format("YYYY-MM-DD");
            filters.to_date = dateRange[1].format("YYYY-MM-DD");
        }
        const res = await fetch(AI_CALLER_CALLS_URL, filters);
        if (res?.status === "success") {
            setCalls(res.data || []);
            setTotalCount(res.total_count || 0);
        }
        setLoading(false);
    };

    useEffect(() => { loadCalls(); }, [page, statusFilter, outcomeFilter, dateRange]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleInitiateCall = async () => {
        if (!customerId.trim()) {
            ToastMsg("error", "Please enter a customer ID");
            return;
        }
        setInitiating(true);
        const res = await create(AI_CALLER_CALL_INITIATE_URL, { customer_id: customerId.trim() });
        setInitiating(false);
        if (res?.status === "success") {
            ToastMsg("success", "Call initiated");
            setCallModalOpen(false);
            setCustomerId("");
            loadCalls();
        } else {
            ToastMsg("error", res?.message || "Failed to initiate call");
        }
    };

    // ── table columns ─────────────────────────────────────────────────────────

    const columns = [
        {
            title: "Customer Name",
            dataIndex: "customer_name",
            key: "customer_name",
            render: (text) => <span className="font-medium text-gray-800 dark:text-gray-100">{text || "—"}</span>,
        },
        {
            title: "Phone",
            dataIndex: "phone",
            key: "phone",
        },
        {
            title: "Campaign",
            dataIndex: "campaign_name",
            key: "campaign_name",
            render: (val) => val || "—",
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status) => (
                <Badge status={STATUS_BADGE[status] || "default"} text={status?.replace("_", " ") || "—"} />
            ),
        },
        {
            title: "Outcome",
            dataIndex: "outcome",
            key: "outcome",
            render: (outcome) => outcome ? (
                <Tag color={OUTCOME_COLORS[outcome] || "default"}>
                    {outcome.replace("_", " ").toUpperCase()}
                </Tag>
            ) : "—",
        },
        {
            title: "Language",
            dataIndex: "language",
            key: "language",
            render: (val) => val || "—",
        },
        {
            title: "Duration",
            dataIndex: "duration_seconds",
            key: "duration_seconds",
            render: (val) => formatDuration(val),
        },
        {
            title: "Cost",
            dataIndex: "total_cost_inr",
            key: "total_cost_inr",
            align: "right",
            render: (val, record) => (
                <span
                    className="font-medium text-teal-700 hover:underline"
                    onClick={(e) => { e.stopPropagation(); setCostModalCall(record); }}
                    title="Click for breakdown"
                >
                    {formatInr(val)}
                </span>
            ),
        },
        {
            title: "Date",
            dataIndex: "created_at",
            key: "created_at",
            render: (val) => val ? dayjs(val).format("DD MMM YYYY, hh:mm A") : "—",
        },
    ];

    return (
        <ContainerCard>
            {/* Header */}
            <div className="flex items-center justify-between mb-4 p-2">
                <div>
                    <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                        <Phone className="h-5 w-5 text-teal-600" /> Call Logs
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">All AI caller call records</p>
                </div>
                <Button
                    onClick={() => setCallModalOpen(true)}
                    className="flex items-center gap-2 text-sm"
                >
                    <PhoneCall className="h-4 w-4" /> Initiate Call
                </Button>
            </div>

            {/* Filter bar */}
            <div className="flex flex-wrap items-center gap-3 mb-4 px-2">
                <RangePicker
                    value={dateRange}
                    onChange={(dates) => { setDateRange(dates); setPage(1); }}
                    allowClear
                    className="w-64"
                />
                <Select
                    value={statusFilter}
                    onChange={(val) => { setStatusFilter(val); setPage(1); }}
                    options={STATUS_OPTIONS}
                    className="w-40"
                    placeholder="Status"
                />
                <Select
                    value={outcomeFilter}
                    onChange={(val) => { setOutcomeFilter(val); setPage(1); }}
                    options={OUTCOME_OPTIONS}
                    className="w-44"
                    placeholder="Outcome"
                />
            </div>

            {/* Table */}
            <div className="px-2 mb-2">
                <Table
                    columns={columns}
                    dataSource={calls}
                    rowKey="id"
                    loading={loading}
                    size="small"
                    locale={{ emptyText: "No call logs found" }}
                    pagination={{
                        current: page,
                        pageSize: limit,
                        total: totalCount,
                        onChange: (p) => setPage(p),
                        showSizeChanger: false,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                    }}
                    onRow={(record) => ({
                        onClick: () => navigate(`/ai-caller/calls/${record.id}`),
                        className: "cursor-pointer",
                    })}
                />
            </div>

            {/* Initiate Call Modal */}
            <Modal
                title="Initiate AI Call"
                open={callModalOpen}
                onCancel={() => { setCallModalOpen(false); setCustomerId(""); }}
                onOk={handleInitiateCall}
                confirmLoading={initiating}
                okText="Call"
            >
                <div className="py-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Customer ID
                    </label>
                    <Input
                        placeholder="Enter customer ID"
                        value={customerId}
                        onChange={(e) => setCustomerId(e.target.value)}
                        onPressEnter={handleInitiateCall}
                    />
                </div>
            </Modal>

            <CallCostBreakdown
                open={!!costModalCall}
                onClose={() => setCostModalCall(null)}
                call={costModalCall}
            />
        </ContainerCard>
    );
}
